import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CircleCheck, Building2, Mail, ArrowRight } from 'lucide-react';

/* ----------------------------------------------------------------------------
 * Request-demo page — neutral base, single signal-green accent, editorial serif
 * headings, hairline-bordered card. Form logic is preserved verbatim; only the
 * presentation matches the AutoHire design system (see Home.js).
 * -------------------------------------------------------------------------- */

const GREEN = '#2563EB';

const reveal = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-80px' },
};

const COVERAGE = [
    'Live tour of the AI Sourcing engine',
    'Parsing your actual job descriptions',
    'Generating outreach campaigns in real-time',
    'Pricing and ROI analysis for your team',
];

const inputClass =
    'w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-3 text-gray-900 dark:text-white placeholder-gray-400 transition-colors focus:outline-none focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB]';

function RequestDemo() {
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
    };

    if (submitted) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC] px-4 pt-24 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.45, ease: 'easeOut' }}
                    className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_48px_-24px_rgba(15,23,42,0.18)] dark:border-gray-800 dark:bg-gray-900"
                >
                    <div
                        className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full"
                        style={{ background: '#EFF4FE', color: GREEN }}
                    >
                        <CircleCheck size={32} strokeWidth={1.8} />
                    </div>
                    <h2 className="font-serif text-2xl font-semibold text-gray-900 dark:text-white">
                        Request received
                    </h2>
                    <p className="mt-4 text-gray-600 dark:text-gray-300">
                        Thanks for your interest. A product specialist will email you within 1 business day to schedule your walkthrough.
                    </p>
                    <button
                        onClick={() => (window.location.href = '/')}
                        className="mt-7 inline-flex items-center justify-center gap-1.5 text-sm font-semibold transition-colors"
                        style={{ color: GREEN }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = '#1D4FD8')}
                        onMouseLeave={(e) => (e.currentTarget.style.color = GREEN)}
                    >
                        Back to home <ArrowRight size={15} />
                    </button>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8FAFC] pb-24 pt-24 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
            <div className="mx-auto flex max-w-7xl flex-col gap-16 px-6 lg:flex-row lg:px-8">

                {/* Left Col */}
                <div className="lg:w-1/2">
                    <motion.div
                        {...reveal}
                        transition={{ duration: 0.5 }}
                        className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em]"
                        style={{ color: GREEN }}
                    >
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: GREEN }} />
                        Book a demo
                    </motion.div>
                    <motion.h1
                        {...reveal}
                        transition={{ duration: 0.55, delay: 0.05 }}
                        className="mt-5 font-serif text-4xl font-semibold leading-[1.05] tracking-tight text-gray-900 dark:text-white md:text-5xl"
                    >
                        See AutoHire in action
                    </motion.h1>
                    <motion.p
                        {...reveal}
                        transition={{ duration: 0.55, delay: 0.1 }}
                        className="mt-6 max-w-xl text-lg leading-relaxed text-gray-600 dark:text-gray-300"
                    >
                        Book a personalized 15-minute demo to see how our AI agents can automate your specific hiring workflow.
                    </motion.p>

                    <motion.div {...reveal} transition={{ duration: 0.55, delay: 0.16 }} className="mt-12">
                        <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-gray-500">
                            What we'll cover
                        </h3>
                        <ul className="mt-5 space-y-4">
                            {COVERAGE.map((item, i) => (
                                <li key={i} className="flex items-start gap-4">
                                    <div
                                        className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs font-bold"
                                        style={{ background: '#EFF4FE', color: '#1D4FD8', border: '1px solid #BFDBFE' }}
                                    >
                                        {i + 1}
                                    </div>
                                    <span className="text-lg text-gray-700 dark:text-gray-300">{item}</span>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-10 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
                            <div className="mb-4 flex -space-x-3 overflow-hidden">
                                {[1, 2, 3].map((i) => (
                                    <div
                                        key={i}
                                        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-xs font-semibold ring-2 ring-white dark:ring-gray-900"
                                        style={{ background: '#EFF4FE', color: '#1D4FD8' }}
                                    >
                                        {/* Placeholder Avatar */}
                                        User
                                    </div>
                                ))}
                            </div>
                            <p className="text-[15px] leading-relaxed text-gray-700 dark:text-gray-200">
                                "Best demo I've taken all year. No fluff, just showed me exactly how much time I'd save."
                            </p>
                            <div className="mt-3 text-sm font-semibold text-gray-900 dark:text-white">VP of HR @ GrowthCo</div>
                        </div>
                    </motion.div>
                </div>

                {/* Right Col: Form */}
                <div className="lg:w-1/2">
                    <motion.form
                        {...reveal}
                        transition={{ duration: 0.6, delay: 0.12 }}
                        onSubmit={handleSubmit}
                        className="rounded-2xl border border-gray-200 bg-white p-8 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_48px_-24px_rgba(15,23,42,0.18)] dark:border-gray-800 dark:bg-gray-900"
                    >
                        <div className="space-y-6">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Work Email</label>
                                <div className="relative">
                                    <Mail size={16} className="absolute left-3 top-3.5 text-gray-400" />
                                    <input
                                        type="email"
                                        required
                                        className={`${inputClass} pl-10`}
                                        placeholder="you@company.com"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">First Name</label>
                                    <input type="text" required className={inputClass} />
                                </div>
                                <div>
                                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Last Name</label>
                                    <input type="text" required className={inputClass} />
                                </div>
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Company Name</label>
                                <div className="relative">
                                    <Building2 size={16} className="absolute left-3 top-3.5 text-gray-400" />
                                    <input type="text" required className={`${inputClass} pl-10`} />
                                </div>
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Team Size</label>
                                <select className={inputClass}>
                                    <option>1-10 Employees</option>
                                    <option>11-50 Employees</option>
                                    <option>51-200 Employees</option>
                                    <option>201-1000 Employees</option>
                                    <option>1000+ Employees</option>
                                </select>
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Anything specific you want to see?</label>
                                <textarea rows="3" className={inputClass}></textarea>
                            </div>

                            <button
                                type="submit"
                                className="w-full rounded-lg py-3.5 text-base font-semibold text-white transition-colors"
                                style={{ background: GREEN }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = '#1D4FD8')}
                                onMouseLeave={(e) => (e.currentTarget.style.background = GREEN)}
                            >
                                Request Demo
                            </button>
                        </div>

                        <p className="mt-6 text-center text-xs text-gray-500">
                            By clicking Request Demo, you agree to our Terms and have read our Privacy Policy.
                        </p>
                    </motion.form>
                </div>

            </div>
        </div>
    );
}

export default RequestDemo;
